import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { listSites } from '../../api/sites';
import { listLoanableAssets } from '../../api/assets';
import { createLoanRequest } from '../../api/loans';
import { useAuth } from '../../auth/useAuth';
import { primaryOrgId } from '../../auth/permissions';
import { apiErrorMessage } from '../../lib/api';
import { useToast } from '../../components/ui/Toast';
import { Modal } from '../../components/ui/Modal';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Button } from '../../components/ui/Button';
import { Alert } from '../../components/ui/Alert';
import { Spinner } from '../../components/ui/Spinner';

// Tomorrow, as a sensible earliest default due date - the backend just
// requires "in the future", but a same-day due date isn't a realistic loan.
function tomorrowIso() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
}

export function RequestLoanModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { user } = useAuth();
  const orgId = primaryOrgId(user);
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  const [siteId, setSiteId] = useState('');
  const [assetId, setAssetId] = useState('');
  const [dueDate, setDueDate] = useState(tomorrowIso());
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  const sitesQuery = useQuery({
    queryKey: ['sites', orgId],
    queryFn: () => listSites(orgId!),
    enabled: !!orgId && open,
  });
  const sites = sitesQuery.data ?? [];
  const effectiveSiteId = siteId || sites[0]?.id || '';

  const loanableQuery = useQuery({
    queryKey: ['loanable-assets', effectiveSiteId],
    queryFn: () => listLoanableAssets(effectiveSiteId),
    enabled: !!effectiveSiteId && open,
  });
  const loanableAssets = loanableQuery.data ?? [];

  const effectiveAssetId = useMemo(
    () => (loanableAssets.some((a) => a.id === assetId) ? assetId : ''),
    [loanableAssets, assetId],
  );

  const mutation = useMutation({
    mutationFn: () => createLoanRequest({ assetId: effectiveAssetId, dueDate, notes: notes.trim() || undefined }),
    onSuccess: (loan) => {
      queryClient.invalidateQueries({ queryKey: ['loans'] });
      queryClient.invalidateQueries({ queryKey: ['loans-queue'] });
      showToast(
        loan.status === 'APPROVED'
          ? 'Loan request submitted and auto-approved.'
          : 'Loan request submitted for approval.',
      );
      setAssetId('');
      setNotes('');
      setDueDate(tomorrowIso());
      onClose();
    },
    onError: (err) => setError(apiErrorMessage(err, 'Could not submit the loan request.')),
  });

  function handleSubmit() {
    setError(null);
    if (!effectiveAssetId) {
      setError('Choose an item to borrow.');
      return;
    }
    if (!dueDate) {
      setError('Choose a due date.');
      return;
    }
    mutation.mutate();
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Request a loan"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button isLoading={mutation.isPending} onClick={handleSubmit}>
            Submit request
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error && <Alert tone="error">{error}</Alert>}

        {sites.length > 1 && (
          <Select
            label="Site"
            value={effectiveSiteId}
            onChange={(e) => {
              setSiteId(e.target.value);
              setAssetId('');
            }}
          >
            {sites.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </Select>
        )}

        {loanableQuery.isLoading ? (
          <div className="py-6 text-center">
            <Spinner />
          </div>
        ) : loanableAssets.length === 0 ? (
          <p className="text-sm text-slate-500">Nothing spare to borrow at this site right now.</p>
        ) : (
          <Select label="Item" value={effectiveAssetId} onChange={(e) => setAssetId(e.target.value)}>
            <option value="">Select an item...</option>
            {loanableAssets.map((a) => (
              <option key={a.id} value={a.id}>
                {a.orderLine?.customerCatalogItem?.name ?? 'Asset'}
                {a.serialNumber ? ` (${a.serialNumber})` : ''}
              </option>
            ))}
          </Select>
        )}

        <Input
          label="Due back by"
          type="date"
          min={tomorrowIso()}
          value={dueDate}
          onChange={(e) => setDueDate(e.target.value)}
        />

        <Input
          label="Notes (optional)"
          placeholder="What's this for?"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </div>
    </Modal>
  );
}
